import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/admin", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-dq-bg px-4">
      <SEO title="Admin Login" description="DiziTeQ admin login" path="/admin/login" />
      <div className="mesh-hero pointer-events-none absolute inset-0" aria-hidden="true" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-[2rem] border border-dq-line bg-white p-8 dq-shadow-soft sm:p-10"
      >
        <Link to="/" className="mb-8 flex items-center gap-2.5">
          <span className="grid h-9 w-9 place-items-center rounded-xl bg-dq-ink font-display text-lg font-extrabold text-white">D</span>
          <span className="font-display text-lg font-extrabold text-dq-ink">DiziTeQ</span>
        </Link>
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-dq-orange/10 text-dq-orange">
          <Lock className="h-6 w-6" />
        </span>
        <h1 className="mt-5 font-display text-2xl font-extrabold text-dq-ink">Admin sign in</h1>
        <p className="mt-1 text-sm text-dq-muted">Access your leads and content dashboard.</p>

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email"
            className="w-full rounded-2xl border border-dq-line bg-dq-bg px-4 py-3.5 text-sm focus:border-dq-orange focus:outline-none focus:ring-2 focus:ring-dq-orange/20" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password"
            className="w-full rounded-2xl border border-dq-line bg-dq-bg px-4 py-3.5 text-sm focus:border-dq-orange focus:outline-none focus:ring-2 focus:ring-dq-orange/20" />
          {error && <p className="text-sm font-medium text-dq-orange" data-testid="admin-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-primary w-full disabled:opacity-60">
            {loading ? "Signing in..." : "Sign in"} <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </motion.div>
    </div>
  );
}
